import { LoadingScreen } from '@/core/ui/components';
import type {
  IAdminAuthRepository,
  IAdminNotificationRepository,
  IAdminUserRepository,
  IApiKeyRepository,
  IAuditAdminRepository,
  IBillingAdminRepository,
  IBulkOperationRepository,
  IAccessLevelRepository,
  IAccessDoorRepository,
  ICameraRepository,
  IHikCentralImportRepository,
  IHikCentralSuperConfigRepository,
  IIntegrationCatalogRepository,
  IDashboardAdminRepository,
  IEmailTemplateRepository,
  IFileRepository,
  IIntegrationAdminRepository,
  INotificationRepository,
  ITenantAdminRepository,
} from '@yawi/domain';
import {
  ApiAdminAuthRepository,
  ApiAdminNotificationRepository,
  ApiAdminUserRepository,
  ApiApiKeyRepository,
  ApiAuditAdminRepository,
  ApiBillingAdminRepository,
  ApiBulkOperationRepository,
  ApiAccessLevelRepository,
  ApiAccessDoorRepository,
  ApiCameraRepository,
  ApiHikCentralImportRepository,
  ApiHikCentralSuperConfigRepository,
  ApiIntegrationCatalogRepository,
  ApiDashboardAdminRepository,
  ApiEmailTemplateRepository,
  ApiFileRepository,
  ApiIntegrationAdminRepository,
  ApiNotificationRepository,
  ApiTenantAdminRepository,
  initApiClient,
  setOnRateLimitedCallback,
  setOnTokenCompromisedCallback,
} from '@yawi/infrastructure';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { safeStorage } from '@/shared/utils/safeStorage';
import { useToast } from './ToastContext';

interface ServiceContextValue {
  authRepository: IAdminAuthRepository;
  tenantRepository: ITenantAdminRepository;
  apiKeyRepository: IApiKeyRepository;
  billingRepository: IBillingAdminRepository;
  fileRepo: IFileRepository;
  dashboardRepository: IDashboardAdminRepository;
  adminUserRepository: IAdminUserRepository;
  auditRepository: IAuditAdminRepository;
  integrationRepository: IIntegrationAdminRepository;
  emailTemplateRepository: IEmailTemplateRepository;
  adminNotificationRepository: IAdminNotificationRepository;
  notificationRepo: INotificationRepository;
  bulkOperationRepository: IBulkOperationRepository;
  hikCentralImportRepository: IHikCentralImportRepository;
  hikCentralSuperConfigRepository: IHikCentralSuperConfigRepository;
  accessLevelRepository: IAccessLevelRepository;
  integrationCatalogRepository: IIntegrationCatalogRepository;
  accessDoorRepository: IAccessDoorRepository;
  cameraRepository: ICameraRepository;
}

const ServiceContext = createContext<ServiceContextValue | null>(null);

// Get API base URL from environment or default
const API_BASE_URL = (typeof process !== 'undefined' && process.env?.API_BASE_URL)
  || import.meta.env?.VITE_API_URL
  || 'http://localhost:3000/api/';

export function ServiceProvider({ children }: { children: ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    // Initialize API client on mount
    initApiClient({
      baseUrl: API_BASE_URL,
      onUnauthorized: () => {
        // Clear local storage and reload
        safeStorage.removeItem('yawi-admin-auth');
        window.location.reload();
      },
    });
    setIsInitialized(true);
  }, []);

  // Configurar callbacks de seguridad para rate limiting y token comprometido
  useEffect(() => {
    setOnRateLimitedCallback((retryAfter) => {
      const seconds = retryAfter || 60;
      showToast('warning', `Demasiados intentos. Espera ${seconds} segundos.`);
    });

    setOnTokenCompromisedCallback(() => {
      showToast('error', 'Tu sesión fue cerrada por seguridad. Por favor inicia sesión nuevamente.');
      safeStorage.removeItem('yawi-admin-auth');
      window.location.reload();
    });
  }, [showToast]);

  const services = useMemo<ServiceContextValue>(() => ({
    authRepository: new ApiAdminAuthRepository(),
    tenantRepository: new ApiTenantAdminRepository(),
    apiKeyRepository: new ApiApiKeyRepository(),
    billingRepository: new ApiBillingAdminRepository(),
    fileRepo: new ApiFileRepository(),
    dashboardRepository: new ApiDashboardAdminRepository(),
    adminUserRepository: new ApiAdminUserRepository(),
    auditRepository: new ApiAuditAdminRepository(),
    integrationRepository: new ApiIntegrationAdminRepository(),
    emailTemplateRepository: new ApiEmailTemplateRepository(),
    adminNotificationRepository: new ApiAdminNotificationRepository(),
    notificationRepo: new ApiNotificationRepository(),
    bulkOperationRepository: new ApiBulkOperationRepository(),
    hikCentralImportRepository: new ApiHikCentralImportRepository(),
    hikCentralSuperConfigRepository: new ApiHikCentralSuperConfigRepository(),
    accessLevelRepository: new ApiAccessLevelRepository(),
    integrationCatalogRepository: new ApiIntegrationCatalogRepository(),
    accessDoorRepository: new ApiAccessDoorRepository(),
    cameraRepository: new ApiCameraRepository(),
  }), []);

  if (!isInitialized) {
    return <LoadingScreen message="Initializing..." />;
  }

  return (
    <ServiceContext.Provider value={services}>
      {children}
    </ServiceContext.Provider>
  );
}

export function useServices() {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
}
